import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import CopyButton from '@/components/admin/CopyButton';
import { useFetch } from '@/hooks/useFetch';
import { hasDetails, loadPaymentSettings, safeLink } from '@/shared/paymentSettings';
import { money, paymentLabel } from '@/shared/pricing';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { C } from '@/shared/locales/checkout/keys';
import { around } from '@/shared/locales/pages/keys';

function Row({ label, value, copy, mono }) {
  const { t } = useT();
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={cn('min-w-0 break-all text-sm', mono && 'font-mono')}>{value}</span>
        {copy && <CopyButton text={String(value)} label={t(C.copy)} size="sm" variant="ghost" />}
      </span>
    </div>
  );
}

/** What the customer sees once a payment method is set up: the card without any data loading. */
export function PaymentInstructionsCard({ setting, method, amount, reference, className }) {
  const { t } = useT();
  const link = safeLink(setting?.link_c);
  const [refPre, refPost] = around(t(C.putRef), 'ref');
  return (
    <div className={cn('rounded-lg border border-primary/30 bg-primary/5 p-4 text-start', className)}>
      <p className="flex items-center gap-2 text-sm font-semibold">
        <ApperIcon name="Wallet" size={16} className="text-primary" />
        {t(C.howPay, { method: t(paymentLabel(method)) })}
      </p>
      <div className="mt-2 divide-y divide-border">
        <Row label={t(C.amount)} value={amount != null ? `${money(amount)} USD` : ''} />
        <Row label={t(C.payTo)} value={setting?.pay_to_c} copy mono />
        <Row label={t(C.reference)} value={reference} copy mono />
      </div>
      {setting?.instructions_c && (
        <p className="mt-3 whitespace-pre-line text-sm leading-6 text-muted-foreground">{setting.instructions_c}</p>
      )}
      {reference && (
        <p className="mt-3 text-xs text-muted-foreground">
          {refPre}
          <span className="font-mono text-foreground">{reference}</span>
          {refPost}
        </p>
      )}
      {link && (
        <Button asChild size="sm" className="mt-4">
          <a href={link} target="_blank" rel="noopener noreferrer">
            <ApperIcon name="ExternalLink" size={16} />
            {t(C.openLink)}
          </a>
        </Button>
      )}
    </div>
  );
}

/**
 * Payment instructions for one order, read from the owner's Payment methods settings.
 * Falls back to `fallback` (or nothing) when that method has no details saved yet.
 */
export default function PaymentInstructions({ method, amount, reference, fallback = null, className }) {
  const { data, loading } = useFetch(() => loadPaymentSettings().catch(() => []), []);

  if (loading && data === undefined) return <Skeleton className={cn('h-28 w-full', className)} />;

  const setting = (data ?? []).find((s) => s.Name === method);
  if (!hasDetails(setting)) return fallback;

  return (
    <PaymentInstructionsCard
      setting={setting}
      method={method}
      amount={amount}
      reference={reference}
      className={className}
    />
  );
}
